import { createContext, useContext, useState, useEffect } from "react";
import { apiRequest, uploadDocument } from "utils/api";
import { LoanContext } from "./loan";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import _ from "underscore";
import { useQueryClient } from "@tanstack/react-query";
import { useChecklist } from "hooks/useChecklist";

const ChecklistContext = createContext<{
  grouped: any[];
  editItem: any;
  setEditItem: (item: any) => void;
  reorder: boolean;
  setReorder: (reorder: boolean) => void;
  toggleItem: (id: string, completed: boolean) => void;
  saveItem: (item: any) => void;
  invalidate: () => void;
  isLoading: boolean;
  categories: any[];
  statuses: any[];
  assignContactsToCategory: (item: any) => void;
  loan: any;
  items: any[];
}>({
  grouped: [],
  editItem: null,
  setEditItem: () => {},
  reorder: false,
  setReorder: () => {},
  toggleItem: () => {},
  saveItem: () => {},
  invalidate: () => {},
  isLoading: false,
  categories: [],
  statuses: [],
  assignContactsToCategory: () => {},
  loan: null,
  items: [],
});

const ChecklistConsumer = ChecklistContext.Consumer;

function ChecklistProvider({ children }: { children: JSX.Element }) {
  const { loan: contextLoan } = useContext(LoanContext) ?? {};
  const { user } = useTenant();
  const [editItem, setEditItem] = useState<any | null>(null);
  const [reorder, setReorder] = useState(false);
  const queryClient = useQueryClient();

  const [grouped, setGrouped] = useState<any[]>([]);
  const {
    loan_items: items,
    invalidate,
    isLoading,
    categories,
    statuses,
  } = useChecklist({
    loan_id: contextLoan?.id,
  });

  const toggleItem = async (id: string, completed: boolean) => {
    try {
      await apiRequest("/supabase/loan_items/update", {
        id,
        completed,
      });
      queryClient.setQueryData(["loan_items", contextLoan?.id], (oldData: any) =>
        oldData.map((item: any) => {
          return item.id === id
            ? {
                ...item,
                completed,
              }
            : item;
        })
      );
    } catch (error) {
      console.error("Failed to toggle item:", error);
    }
  };

  const saveItem = async ({
    title,
    category,
    notes,
    documents,
    status,
    contacts,
    last_status,
    start_date,
    due_date,
    description,
  }: {
    title: string;
    category: string;
    notes: string;
    documents: any[];
    contacts: any[];
    status: string;
    last_status: string;
    start_date: string;
    due_date: string;
    description: string;
  }) => {
    const addDocuments = documents.filter((document: any) => !document.id);
    const removeDocuments = editItem.documents.filter((document: any) =>
      documents.find((d: any) => d.id === document.id) ? false : true
    );

    for (const file of addDocuments) {
      await uploadDocument({
        file,
        loan_item: editItem.id,
        loan_id: editItem.loan.id,
        category: category,
      });
    }

    await apiRequest("/supabase/documents/delete", {
      ids: removeDocuments.map((document: any) => document.id),
    });

    // Figure out members to add;
    const toAdd = contacts.filter((member: any) =>
      editItem.contacts.find((m: any) => m.id === member.id) ? false : true
    );

    if (toAdd.length > 0) {
      await apiRequest("/supabase/item_contacts/insert", {
        items: toAdd.map((member: any) => ({
          user: member.id,
          item: editItem.id,
          loan: editItem.loan.id,
        })),
      });
    }

    // Figure out members to remove;
    const toRemove = editItem.contacts.filter((contact: any) =>
      contacts.find((c: any) => c.id === contact.id) ? false : true
    );

    if (toRemove.length > 0) {
      await apiRequest("/supabase/item_contacts/delete", {
        ids: toRemove.map((contact: any) => contact.id),
      });
    }

    if (status !== last_status) {
      await apiRequest("/supabase/loan_item_status/insert", {
        loan_item: editItem.id,
        status,
      });
    }

    await apiRequest("/supabase/loan_items/update", {
      id: editItem.id,
      title,
      category,
      notes,
      start_date,
      due_date,
      description,
    });
    invalidate();
    setEditItem(null);
  };

  const assignContactsToCategory = async ({ contacts, item }: { contacts: any[]; item: any }) => {
    const toAdd = contacts
      .filter(
        (contact: any) =>
          !item.contacts.some((existing: any) => existing.user.id === contact.user.id)
      )
      .map((contact: any) => {
        const existingContact =
          item.contacts.find((existing: any) => existing.user.id === contact.user.id) ?? false;

        if (!existingContact) {
          return {
            id: contact.user.id,
            user: contact.user,
          };
        }

        return contact;
      });

    if (toAdd.length > 0) {
      await apiRequest("/supabase/item_contacts/insert", {
        items: toAdd.map((member: any) => ({
          user: member.id,
          item: item.id,
          loan: item.loan.id,
        })),
      });
    }

    // Figure out members to remove;
    const toRemove = item.contacts.filter((contact: any) =>
      contacts.find((c: any) => c.user.id === contact.user.id) ? false : true
    );

    if (toRemove.length > 0) {
      await apiRequest("/supabase/item_contacts/delete", {
        ids: toRemove.map((contact: any) => contact.id),
      });
    }

    await apiRequest("/supabase/loan_items/update", {
      id: item.id,
      title: item.title,
      category: item.category,
      notes: item.notes,
    });
    invalidate();
  };

  useEffect(() => {
    if (!items || items.length === 0) {
      return;
    }

    if (!user || !user.role || !user.role.id) {
      return;
    }

    // Create a map to ensure unique items by ID
    const uniqueItems = new Map();

    // Only include items with non-empty titles
    items.forEach((item: any) => {
      if (!uniqueItems.has(item.id) && item.title && item.title.trim() !== "") {
        uniqueItems.set(item.id, item);
      }
    });

    const uniqueItemsArray = Array.from(uniqueItems.values());

    const categoryMap = _.groupBy(
      uniqueItemsArray,
      (item: any) => item.category || "Uncategorized"
    );

    const newList = Object.keys(categoryMap)
      .map(category => {
        const categoryItems = categoryMap[category];

        const filteredItems = categoryItems.filter((item: any) => {
          if (!item.title || item.title.trim() === "") {
            return false;
          }

          const hasDirectAssignments =
            item.assignments && Array.isArray(item.assignments) && item.assignments.length > 0;

          const hasChecklistItemAssignments =
            item.checklist_item &&
            item.checklist_item.assignments &&
            Array.isArray(item.checklist_item.assignments) &&
            item.checklist_item.assignments.length > 0;

          if (!hasDirectAssignments && !hasChecklistItemAssignments) {
            return true;
          }

          if (hasDirectAssignments) {
            const isDirectlyAssigned = item.assignments.includes(user.role.id);
            if (isDirectlyAssigned) {
              return true;
            }
          }

          if (hasChecklistItemAssignments) {
            const isAssignedViaChecklistItem = item.checklist_item.assignments.includes(
              user.role.id
            );
            return isAssignedViaChecklistItem;
          }

          return false;
        });

        if (filteredItems.length === 0) {
          return null;
        }

        return {
          category,
          items: filteredItems,
        };
      })
      .filter(group => group !== null)
      .filter(group => group!.items.length > 0);

    setGrouped(newList);
  }, [items, user, user.role.id]);

  return (
    <ChecklistContext.Provider
      value={{
        grouped,
        editItem,
        setEditItem,
        reorder,
        setReorder,
        toggleItem,
        saveItem,
        invalidate,
        isLoading,
        categories,
        statuses,
        assignContactsToCategory,
        loan: contextLoan,
        items: items || [],
      }}
    >
      {children}
    </ChecklistContext.Provider>
  );
}
export { ChecklistContext, ChecklistConsumer, ChecklistProvider };
